import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import opportunityService, {
  Opportunity,
  OpportunityInput,
  OpportunityActivity,
  OpportunityRegisterFilters,
  OpportunityRegisterResponse,
  OpportunityRegisterStats,
  OpportunitySavedView,
} from '@/services/opportunity.service';

export const OPPORTUNITY_KEYS = {
  all: ['crm', 'opportunities'] as const,
  lists: () => [...OPPORTUNITY_KEYS.all, 'list'] as const,
  list: (filters?: Record<string, string | undefined>) => [...OPPORTUNITY_KEYS.lists(), filters] as const,
  pipeline: () => [...OPPORTUNITY_KEYS.all, 'pipeline'] as const,
  details: () => [...OPPORTUNITY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...OPPORTUNITY_KEYS.details(), id] as const,
  activities: (id: string) => [...OPPORTUNITY_KEYS.detail(id), 'activities'] as const,
  register: (filters?: OpportunityRegisterFilters) => [...OPPORTUNITY_KEYS.all, 'register', filters] as const,
  registerStats: (filters?: OpportunityRegisterFilters) => [...OPPORTUNITY_KEYS.all, 'register-stats', filters] as const,
  savedViews: () => [...OPPORTUNITY_KEYS.all, 'saved-views'] as const,
};

export function useOpportunities(filters?: {
  stage?: string;
  status?: string;
  sales_owner?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: OPPORTUNITY_KEYS.list(filters),
    queryFn: () => opportunityService.getAll(filters),
  });
}

export function useOpportunityPipeline() {
  return useQuery({
    queryKey: OPPORTUNITY_KEYS.pipeline(),
    queryFn: () => opportunityService.getPipeline(),
  });
}

export function useOpportunityRegister(filters: OpportunityRegisterFilters) {
  return useQuery({
    queryKey: OPPORTUNITY_KEYS.register(filters),
    queryFn: () => opportunityService.getRegister(filters),
  });
}

export function useOpportunityRegisterStats(filters: OpportunityRegisterFilters) {
  return useQuery({
    queryKey: OPPORTUNITY_KEYS.registerStats(filters),
    queryFn: () => opportunityService.getRegisterStats(filters),
  });
}

export function useOpportunitySavedViews() {
  return useQuery({
    queryKey: OPPORTUNITY_KEYS.savedViews(),
    queryFn: () => opportunityService.getSavedViews(),
  });
}

export function useCreateOpportunitySavedView() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (payload: Partial<OpportunitySavedView>) => opportunityService.createSavedView(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.savedViews() });
      message.success('Saved view created');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to create saved view'),
  });
}

export function useUpdateOpportunitySavedView() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<OpportunitySavedView> }) =>
      opportunityService.updateSavedView(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.savedViews() });
      message.success('Saved view updated');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to update saved view'),
  });
}

export function useDeleteOpportunitySavedView() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (id: string) => opportunityService.deleteSavedView(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.savedViews() });
      message.success('Saved view deleted');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to delete saved view'),
  });
}

export function useExportOpportunityRegister() {
  const { message } = App.useApp();
  return useMutation({
    mutationFn: async (filters: OpportunityRegisterFilters) => {
      const blob = await opportunityService.exportRegister(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'opportunity-register.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => message.success('Export downloaded'),
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to export register'),
  });
}

export function useOpportunity(id: string | undefined) {
  return useQuery({
    queryKey: OPPORTUNITY_KEYS.detail(id!),
    queryFn: () => opportunityService.getById(id!),
    enabled: !!id,
  });
}

export function useOpportunityActivities(opportunityId: string | undefined) {
  return useQuery({
    queryKey: OPPORTUNITY_KEYS.activities(opportunityId!),
    queryFn: () => opportunityService.getActivities(opportunityId!),
    enabled: !!opportunityId,
  });
}

export function useCreateOpportunity() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (data: OpportunityInput) => opportunityService.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.lists() });
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.pipeline() });
      message.success('Opportunity created');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to create'),
  });
}

export function useUpdateOpportunity() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<OpportunityInput> }) =>
      opportunityService.update(id, data),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.lists() });
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.detail(v.id) });
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.pipeline() });
      message.success('Opportunity updated');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to update'),
  });
}

export function useDeleteOpportunity() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (id: string) => opportunityService.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.lists() });
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.pipeline() });
      message.success('Opportunity deleted');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to delete'),
  });
}

export function useUpdateOpportunityStage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: string }) => opportunityService.updateStage(id, stage),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.lists() });
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.detail(v.id) });
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.pipeline() });
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.activities(v.id) });
      message.success('Stage updated');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to update stage'),
  });
}

export function useMarkOpportunityWon() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, win_reason }: { id: string; win_reason?: string }) =>
      opportunityService.markWon(id, win_reason),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.lists() });
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.detail(v.id) });
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.pipeline() });
      message.success('Marked as won');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed'),
  });
}

export function useMarkOpportunityLost() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, loss_reason }: { id: string; loss_reason: string }) =>
      opportunityService.markLost(id, loss_reason),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.lists() });
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.detail(v.id) });
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.pipeline() });
      message.success('Marked as lost');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed'),
  });
}

export function useConvertOpportunityToProject() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, project_id }: { id: string; project_id: string }) =>
      opportunityService.convertToProject(id, project_id),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.lists() });
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.detail(v.id) });
      qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.pipeline() });
      message.success('Linked to project');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to convert'),
  });
}

export type { Opportunity, OpportunityInput, OpportunityActivity };
